var _excluded = ["aria-describedby", "aria-labelledby", "align", "alignFooter", "children", "className", "closeButton", "closeOnBlur", "expanded", "footer", "i18n", "id", "maxWidth", "onClose", "portal", "portalContainer", "show", "title"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import mergeRefs from 'react-merge-refs';
import Portal from '../shared/portal';
import useStyles from './use_styles';
import useDirection from '../direction/use_direction';
import useLockScroll, { getOwnerDocument } from '../utils/use_lock_scroll';
import UtilityButton from '../shared/utility_button';
import modalI18n from './i18n';
import { jsx as ___EmotionJSX } from "@emotion/core";
var CENTER_ALIGN = 'center';
var LEFT_ALIGN = 'left';
var RIGHT_ALIGN = 'right';
function getDeprecatedAlignment(alignment) {
  var isRTL = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var mapAlignDeprecated = {
    center: CENTER_ALIGN,
    end: RIGHT_ALIGN,
    left: isRTL ? RIGHT_ALIGN : LEFT_ALIGN,
    right: isRTL ? LEFT_ALIGN : RIGHT_ALIGN,
    start: LEFT_ALIGN
  };
  return mapAlignDeprecated[alignment];
}
var Modal = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var {
      'aria-describedby': ariaDescribedByProp,
      'aria-labelledby': ariaLabelledByProp,
      align = 'start',
      alignFooter,
      children,
      className,
      closeButton = false,
      closeOnBlur = true,
      expanded = true,
      footer,
      i18n,
      id,
      maxWidth,
      onClose,
      portal = true,
      portalContainer,
      show = false,
      title
    } = _ref,
    modalProps = _objectWithoutProperties(_ref, _excluded);
  var {
    isRTL
  } = useDirection();
  var modalRef = React.useRef(undefined);
  var handleRef = mergeRefs([modalRef, ref]);
  var backdropClickRef = React.useRef(false);
  var closeModal = React.useCallback(event => {
    if (onClose && show) onClose(event);
  }, [show, onClose]);
  useLockScroll({
    container: getOwnerDocument(modalRef.current).body,
    isRTL,
    lock: show
  });
  React.useEffect(() => {
    var handleKeyDownEvent = event => {
      if (show && event.key === 'Escape') {
        closeModal(event);
      }
    };
    document.addEventListener('keydown', handleKeyDownEvent);
    return () => {
      document.removeEventListener('keydown', handleKeyDownEvent);
    };
  }, [closeModal, show]);
  var handleKeyDown = React.useCallback(event => {
    if (event.target === modalRef.current) {
      event.stopPropagation();
    }
  }, []);
  var handleMouseDown = React.useCallback(event => {
    backdropClickRef.current = event.target === event.currentTarget;
  }, []);
  var handleBackdropClick = React.useCallback(event => {
    if (!backdropClickRef.current) {
      return;
    }
    backdropClickRef.current = false;
    if (closeOnBlur) closeModal(event);
  }, [closeModal, closeOnBlur]);
  var bodyId = id ? "".concat(id, "-desc") : undefined;
  var titleId = id && title ? "".concat(id, "-label") : undefined;
  var ariaDescribedBy = ariaDescribedByProp || bodyId;
  var ariaLabelledBy = ariaLabelledByProp || titleId;
  var style = useStyles({
    align: getDeprecatedAlignment(align, isRTL),
    alignFooter: alignFooter ? getDeprecatedAlignment(alignFooter, isRTL) : undefined,
    expanded,
    show,
    maxWidth
  });
  return ___EmotionJSX(Portal, {
    container: portalContainer,
    disablePortal: !portal
  }, ___EmotionJSX("div", _extends({
    "aria-describedby": ariaDescribedBy,
    "aria-hidden": !show,
    "aria-labelledby": ariaLabelledBy,
    className: className,
    css: style.root,
    onClick: handleBackdropClick,
    onKeyDown: handleKeyDown,
    ref: handleRef,
    role: "presentation"
  }, modalProps), ___EmotionJSX("div", {
    className: "vn-modal--dialog",
    css: style.dialog,
    onMouseDown: handleMouseDown,
    role: "presentation"
  }, ___EmotionJSX("div", {
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    "aria-modal": "true",
    className: "vn-modal--content",
    css: style.content,
    role: "dialog"
  }, title && ___EmotionJSX("h4", {
    className: "vn-modal--title",
    css: style.title,
    id: titleId
  }, title), closeButton && ___EmotionJSX(UtilityButton, {
    className: "vn-modal__close",
    "aria-label": (i18n === null || i18n === void 0 ? void 0 : i18n.close) || modalI18n.close,
    css: style.closeButton,
    onClick: closeModal
  }), ___EmotionJSX("div", {
    className: "vn-modal--body",
    css: style.body,
    id: bodyId
  }, children), footer && ___EmotionJSX("div", {
    className: "vn-modal--footer",
    css: style.footer
  }, footer)))));
});
Modal.displayName = 'Modal';
export default Modal;